import * as Yup from 'yup';
import { htmlToPlainText, normalizeRichTextForSubmit } from '@utils/richText';

// Enhanced validation schema
export const validationTaskSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .required('Name is required'),
  // Rich text (HTML). Length has to be measured on the rendered text, not the markup:
  // an empty Tiptap document is `<p></p>`, which is seven truthy characters and would
  // sail past a plain `.min(3)`, while a real one-word description like `<p>Ok</p>` is
  // nine characters of markup around two of text.
  description: Yup.string()
    .transform((value) => normalizeRichTextForSubmit(value))
    .nullable()
    .test(
      'min-visible-length',
      'Description must be at least 3 characters',
      (value) => value == null || htmlToPlainText(value).trim().length >= 3,
    )
    // Guards the column against a runaway paste; matches the backend's own limit.
    .max(20000, 'Description is too long'),
  taskType: Yup.string().required('Task type is required'),
  status: Yup.string(),
  otherType: Yup.string().when('taskType', {
    is: (value: string) => value === 'Other',
    then: () =>
      Yup.string()
        .min(3, 'Other type must be at least 3 characters')
        .required('Other type is required'),
    otherwise: () => Yup.string().nullable(),
  }),
  startTime: Yup.string().required('Start time is required'),
  endTime: Yup.string()
    .test(
      'end-after-start',
      'End time must be on or after start time',
      function (value) {
        const { startTime } = this.parent;
        if (!startTime || !value) return true;

        // Parse dates without timezone issues
        const startDate = new Date(startTime + 'T00:00:00');
        const endDate = new Date(value + 'T00:00:00');

        return endDate >= startDate;
      },
    )
    .required('End time is required'),
  budget: Yup.number()
    .nullable()
    .max(99999999, 'Budget must be less than 99,999,999')
    .transform((value) => (isNaN(value) || value === '' ? null : value))
    .min(0, 'Budget must be positive'),
  crewIds: Yup.array().nullable(),
  delegateOrganizationId: Yup.string().when('delegationType', {
    is: 'subcontractor',
    then: () => Yup.string().required('Subcontractor is required'),
    otherwise: () => Yup.string().nullable(),
  }),
  // New fields for Assignment tab
  manHours: Yup.number()
    .nullable()
    .transform((value) => (isNaN(value) || value === '' ? null : value))
    .min(0, 'Man hours must be positive'),
  timeConstraint: Yup.number()
    .nullable()
    .transform((value) => (isNaN(value) || value === '' ? null : value))
    .min(0, 'Time constraint must be positive'),
  typeOfTimeConstraint: Yup.string()
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .oneOf(['hours', 'days', 'weeks', null], 'Invalid time type'),
  superintendentId: Yup.string().nullable(),
  note: Yup.string().nullable(),
});
